import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiClock, FiZap, FiMail, FiMessageSquare, FiCloud, FiDatabase, FiCalendar, FiGithub, FiSlack, FiTrello } from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';
import { useAuth } from '../../context/AuthContext';

const Integrations = () => {
  const integrations = [
    {
      name: 'Email',
      description: 'Create tasks from emails and get notifications directly in your inbox.',
      icon: FiMail,
      status: 'available',
      color: 'bg-red-50 text-red-600',
      category: 'Communication'
    },
    {
      name: 'Slack',
      description: 'Get task updates, create tasks, and collaborate without leaving Slack.',
      icon: FiSlack,
      status: 'coming-soon',
      color: 'bg-purple-50 text-purple-600',
      category: 'Communication'
    },
    {
      name: 'Google Calendar',
      description: 'Sync deadlines and scheduled tasks with your Google Calendar.',
      icon: FiCalendar,
      status: 'available',
      color: 'bg-blue-50 text-blue-600',
      category: 'Productivity'
    },
    {
      name: 'GitHub',
      description: 'Link commits, PRs, and issues to your tasks for seamless development tracking.',
      icon: FiGithub,
      status: 'coming-soon',
      color: 'bg-gray-100 text-gray-700',
      category: 'Development'
    },
    {
      name: 'Webhooks',
      description: 'Connect Tasklina to any app with custom webhook integrations.',
      icon: FiZap,
      status: 'available',
      color: 'bg-yellow-50 text-yellow-600',
      category: 'Development'
    },
    {
      name: 'Cloud Storage',
      description: 'Attach files from Google Drive, Dropbox, or OneDrive to your tasks.',
      icon: FiCloud,
      status: 'coming-soon',
      color: 'bg-cyan-50 text-cyan-600',
      category: 'Storage'
    },
    {
      name: 'REST API',
      description: 'Full API access to build custom integrations and automate workflows.',
      icon: FiDatabase,
      status: 'available',
      color: 'bg-green-50 text-green-600',
      category: 'Development'
    },
    {
      name: 'Trello Import',
      description: 'Easily import your boards, lists, and cards from Trello.',
      icon: FiTrello,
      status: 'coming-soon',
      color: 'bg-blue-50 text-blue-600',
      category: 'Migration'
    }
  ];

  const categories = ['All', 'Communication', 'Productivity', 'Development', 'Storage', 'Migration'];
  const [activeCategory, setActiveCategory] = React.useState('All');

  const filteredIntegrations = activeCategory === 'All' 
    ? integrations 
    : integrations.filter(i => i.category === activeCategory);

  const features = [
    {
      icon: FiZap,
      title: 'Easy Setup',
      description: 'Connect your favorite tools in just a few clicks. No coding required.'
    },
    {
      icon: FiMessageSquare,
      title: 'Two-Way Sync',
      description: 'Changes sync automatically between Tasklina and connected apps.'
    },
    {
      icon: FiCloud,
      title: 'Secure Connections',
      description: 'Enterprise-grade security with OAuth and encrypted data transfer.'
    }
  ];

  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <PublicPageHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex p-3 bg-blue-100 rounded-2xl mb-6">
              <FiZap className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Connect Your Favorite Tools
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Integrate Tasklina with the apps you already use to streamline your workflow and boost productivity.
            </p>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="py-12 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map((integration, index) => {
              const Icon = integration.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${integration.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {integration.status === 'available' ? (
                      <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                        <FiCheck className="w-3 h-3" />
                        <span>Available</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                        <FiClock className="w-3 h-3" />
                        <span>Coming Soon</span>
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{integration.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{integration.description}</p>
                  
                  {integration.status === 'available' && (
                    <Link
                      to={currentUser ? "/dashboard" : "/auth/register-options"}
                      className="mt-4 inline-flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors"
                    >
                      <span>{currentUser ? 'Go to Dashboard' : 'Get Started'}</span>
                      <FiArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Build Custom Integrations
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Use our powerful REST API to build custom integrations and automate your workflows. 
                Full documentation, webhooks, and developer support available.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'RESTful API with JSON responses',
                  'Comprehensive API documentation',
                  'Webhook support for real-time updates',
                  'Rate limiting and secure authentication',
                  'SDKs and code examples'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <FiCheck className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
              >
                <span>Request API Access</span>
                <FiArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-gray-900 rounded-2xl p-6 font-mono text-sm overflow-hidden">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <pre className="text-green-400 overflow-x-auto">
                <code>{`// Create a new task
fetch('https://api.tasklina.com/v1/tasks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    title: 'Review project proposal',
    priority: 'high',
    dueDate: '2025-01-20'
  })
})
.then(res => res.json())
.then(task => console.log(task));`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Don't See Your Tool?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            We're always adding new integrations. Let us know what tools you'd like to see integrated with Tasklina.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
          >
            <FiMail className="w-5 h-5" />
            <span>Request an Integration</span>
          </Link>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
};

export default Integrations;
